import { Pressable, Text, View } from "react-native";

import { type Condition } from "@/hooks/useApi";
import { cn } from "@/lib/ui/cn";
import { slopToFloor } from "@/lib/ui/touchTarget";

export const CONDITIONS: Condition[] = ["NM", "LP", "MP", "HP", "DMG"];

/** A chip's painted height (`min-h-9`); slop carries it to the platform touch floor, as on the
 *  scan row's other chips. Condition is the field corrected most often down a scanned stack. */
const CHIP_PAINT = 36;

/**
 * Raw-card condition chip row — NM/LP/MP/HP/DMG. Shared by every surface that records a raw
 * condition (quick-add, the manage sheet, scan-confirm); a graded unit carries a grade instead
 * and never renders this.
 */
export function ConditionPicker(
  { value, onChange, allowClear = true }: {
    value: Condition | null;
    onChange: (condition: Condition | null) => void;
    /** Tapping the active chip clears it back to unset — the default (optional-field)
     *  behavior for quick-add/manage. Off for a required selector (scan-confirm, which
     *  always starts at "NM"), where every line must carry a condition. */
    allowClear?: boolean;
  },
) {
  return (
    <View className="flex-row flex-wrap gap-2">
      {CONDITIONS.map((cnd) => {
        const active = value === cnd;
        return (
          <Pressable
            key={cnd}
            onPress={() => {
              if (active && !allowClear) return;
              onChange(active ? null : cnd);
            }}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            accessibilityLabel={`Condition ${cnd}`}
            hitSlop={slopToFloor(CHIP_PAINT)}
            className={cn(
              "min-h-9 justify-center rounded-full border px-3 py-1.5",
              active ? "border-primary bg-accent-soft" : "border-border bg-surface",
            )}
          >
            <Text
              className={cn(
                "text-xs font-bold",
                active ? "text-primary" : "text-muted-foreground",
              )}
            >
              {cnd}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
